import React, { useState, useEffect } from "react";
import { useAuth } from "../services/AuthContext.js";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  ArrowLeft,
  BookOpen,
  LogOut,
} from "lucide-react";
import "./LessonSidebar.css";

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

async function getCompletedLessons(token) {
  try {
    const response = await fetch(`${baseAPI_URL}/users/get-completed-lessons`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error("Failed to fetch completed lessons");
    const data = await response.json();
    return data.completed_lessons || {};
  } catch (error) {
    console.error("Error getting completed lessons:", error);
    return {};
  }
}

function ProgressBar({ value = 0 }) {
  const clamped = Math.min(100, Math.max(0, Number(value) || 0));

  return (
    <div className="sidebar-progress">
      <div className="sidebar-progress-track">
        <div className="sidebar-progress-fill" style={{ width: `${clamped}%` }} />
      </div>
      <span className="sidebar-progress-label">{clamped}% concluído</span>
    </div>
  );
}

const LessonSidebar = ({ courseId, currentLessonId, onLessonChange, isCompleted }) => {
  const { logout, user, getIdToken } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [completedLessons, setCompletedLessons] = useState({});
  const [percentageCompleted, setPercentageCompleted] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!user) return;

    const loadCompletionData = async () => {
      const token = await getIdToken();
      const data = await getCompletedLessons(token);
      setCompletedLessons(data);
    };

    loadCompletionData();
  }, [courseId, currentLessonId, isCompleted, user, getIdToken]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const token = await getIdToken();
        const res = await fetch(`${baseAPI_URL}/courses/${courseId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error("Course not found");
        const data = await res.json();
        setCourse(data);
      } catch (err) {
        console.error("Error fetching course:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, getIdToken]);

  const isLessonCompleted = (cId, sectionId, lessonId) => {
    return completedLessons?.[cId]?.[sectionId]?.[lessonId] === true;
  };

  useEffect(() => {
    if (!course || !completedLessons) return;

    let courseTotalLength = 0;
    let courseCompletedLength = 0;

    for (let i = 0; i < course.sections.length; i++) {
      for (let j = 0; j < course.sections[i].lessons.length; j++) {
        if (course.sections[i].lessons[j].length) {
          courseTotalLength += Number(course.sections[i].lessons[j].length);
          if (
            isLessonCompleted(
              courseId,
              course.sections[i].code,
              course.sections[i].lessons[j].code
            )
          ) {
            courseCompletedLength += Number(course.sections[i].lessons[j].length);
          }
        }
      }
    }

    if (courseTotalLength > 0) {
      setPercentageCompleted(((100 * courseCompletedLength) / courseTotalLength).toFixed(1));
    } else {
      setPercentageCompleted(0);
    }
  }, [course, completedLessons, courseId]);

  const handleLessonClick = (sectionId, lessonId) => {
    navigate(`/courses/${courseId}/sections/${sectionId}/lessons/${lessonId}`);
    if (onLessonChange) {
      onLessonChange(sectionId, lessonId);
    }
    if (isMobile) {
      setIsCollapsed(true);
    }
  };

  if (loading || !course) return null;

  const showOverlay = isMobile && !isCollapsed;

  return (
    <>
      {isMobile && isCollapsed && (
        <button
          type="button"
          className="mobile-menu-button"
          onClick={() => setIsCollapsed(false)}
          aria-label="Abrir menu do curso"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}

      <div
        className={`lesson-sidebar ${isCollapsed ? "collapsed" : ""} ${isMobile ? "mobile" : ""}`}
      >
        {!isMobile && isCollapsed && (
          <button
            type="button"
            className="expand-button-desktop"
            onClick={() => setIsCollapsed(false)}
            aria-label="Expandir menu do curso"
          >
            <ChevronRight size={28} />
          </button>
        )}
        {!isMobile && !isCollapsed && (
          <button
            type="button"
            className="collapse-button-desktop"
            onClick={() => setIsCollapsed(true)}
            aria-label="Recolher menu do curso"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        {isMobile && !isCollapsed && (
          <button
            type="button"
            className="collapse-button-mobile"
            onClick={() => setIsCollapsed(true)}
            aria-label="Fechar menu do curso"
          >
            <ChevronLeft size={16} />
          </button>
        )}

        {!isCollapsed && (
          <div className="sidebar-content">
            <div className="sidebar-header">
              <button
                type="button"
                className="sidebar-back-link"
                onClick={() => navigate("/allcourses")}
              >
                <ArrowLeft size={14} />
                Todos os cursos
              </button>
              <h3>{course.title}</h3>
              <ProgressBar value={percentageCompleted} />
              <button
                type="button"
                className="sidebar-course-link"
                onClick={() => navigate(`/coursepage/${courseId}`)}
              >
                <BookOpen size={14} />
                Página do curso
              </button>
            </div>

            <div className="course-sections">
              {course.sections.map((section) => (
                <div key={section.code} className="sidebar-section">
                  <h4 className="sidebar-section-title">{section.title}</h4>
                  <ul className="lessons-list">
                    {section.lessons.map((lesson) => {
                      const completed = isLessonCompleted(
                        courseId,
                        section.code,
                        lesson.code
                      );
                      const active = lesson.code === currentLessonId;
                      return (
                        <li
                          key={lesson.code}
                          className={`lesson-item ${active ? "is-current" : ""} ${
                            completed ? "is-completed" : ""
                          }`}
                        >
                          <a
                            href={`/courses/${courseId}/sections/${section.code}/lessons/${lesson.code}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleLessonClick(section.code, lesson.code);
                            }}
                          >
                            {completed ? (
                              <CheckCircle size={16} color={active ? "#ffffff" : "#2e7d32"} />
                            ) : (
                              <Circle size={16} color={active ? "#ffffff" : "#9db3ca"} />
                            )}
                            {lesson.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <div className="sidebar-account">
              <button type="button" className="sidebar-logout-btn" onClick={logout}>
                <LogOut size={16} />
                Sair da conta
              </button>
            </div>
          </div>
        )}
      </div>

      {showOverlay && (
        <button
          type="button"
          className="lesson-sidebar-overlay visible"
          aria-label="Fechar menu do curso"
          onClick={() => setIsCollapsed(true)}
        />
      )}
    </>
  );
};

export default LessonSidebar;
